import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { 
  ArrowLeft, TrendingUp, User, Mail, DollarSign, 
  Calendar, Briefcase, FileText, Send, Eye, Trash2, Download 
} from "lucide-react";

export default function IncrementLetter() {
  const navigate = useNavigate();
  const [showPreview, setShowPreview] = useState(false);
  const [sentLetters, setSentLetters] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("sentIncrementLetters") || "[]");
    } catch {
      return [];
    }
  });

  const [form, setForm] = useState({
    employeeEmail: "",
    employeeId: "",
    employeeName: "",
    designation: "",
    department: "",
    currentSalary: "",
    incrementAmount: "",
    revisedSalary: "",
    effectiveDate: "",
    performanceCycle: "",
    issuedBy: "",
    remarks: "",
  });

  const poppinsStyle = { fontFamily: "'Poppins', sans-serif" };
  const robotoStyle = { fontFamily: "'Roboto', sans-serif" };

  const handleChange = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const calculateRevisedSalary = () => {
    const current = Number(form.currentSalary || 0);
    const increment = Number(form.incrementAmount || 0);
    return current + increment;
  };

  const handleSend = () => {
    if (!form.employeeEmail || !form.employeeName) {
      alert("Employee Name and Email are required");
      return;
    }

    const letter = {
      ...form,
      id: Date.now(),
      revisedSalary: form.revisedSalary || calculateRevisedSalary(),
      sentAt: new Date().toLocaleString(),
    };

    const updated = [letter, ...sentLetters];
    setSentLetters(updated);
    localStorage.setItem("sentIncrementLetters", JSON.stringify(updated));
    setShowPreview(false);
    
    alert(`Increment letter successfully logged for ${form.employeeName}`);
    setForm({
      employeeEmail: "", employeeId: "", employeeName: "", designation: "",
      department: "", currentSalary: "", incrementAmount: "", revisedSalary: "",
      effectiveDate: "", performanceCycle: "", issuedBy: "", remarks: "",
    });
  };

  return (
    <div className="min-h-screen bg-slate-50/50 p-4 md:p-8" style={robotoStyle}>
      <div className="max-w-6xl mx-auto">
        
        {/* Header Section */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
          <div>
            <button 
              onClick={() => navigate("/admin/letter-template-mgmt")}
              className="flex items-center gap-2 text-slate-500 hover:text-slate-900 mb-2 transition-colors text-sm font-medium"
            >
              <ArrowLeft size={16} /> Back to Management
            </button>
            <h1 className="text-2xl font-semibold text-slate-900" style={poppinsStyle}>
              Increment Letter
            </h1>
            <p className="text-sm text-slate-500">Review and issue salary revisions based on performance.</p>
          </div>
          <div className="p-3 bg-indigo-50 rounded-xl text-indigo-600">
            <TrendingUp size={28} />
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Form Column */}
          <div className="lg:col-span-2 space-y-6">
            <div className="bg-white border border-slate-200 rounded-2xl p-6 shadow-sm">
              <h2 className="text-sm font-bold uppercase tracking-wider text-slate-400 mb-6" style={poppinsStyle}>
                Employee & Revision Details
              </h2>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <Input label="Employee ID" value={form.employeeId} onChange={(v) => handleChange("employeeId", v)} />
                <Input label="Employee Name" icon={<User size={14}/>} value={form.employeeName} onChange={(v) => handleChange("employeeName", v)} />
                <Input label="Employee Email" icon={<Mail size={14}/>} type="email" value={form.employeeEmail} onChange={(v) => handleChange("employeeEmail", v)} />
                <Input label="Designation" icon={<Briefcase size={14}/>} value={form.designation} onChange={(v) => handleChange("designation", v)} />
                <Input label="Current Salary" icon={<DollarSign size={14}/>} value={form.currentSalary} onChange={(v) => handleChange("currentSalary", v)} />
                <Input label="Increment Amount" icon={<TrendingUp size={14}/>} value={form.incrementAmount} onChange={(v) => handleChange("incrementAmount", v)} />
                <Input 
                  label="Revised Salary" 
                  value={form.revisedSalary || (form.currentSalary || form.incrementAmount ? calculateRevisedSalary() : "")} 
                  onChange={(v) => handleChange("revisedSalary", v)} 
                />
                <Input label="Effective From" type="date" icon={<Calendar size={14}/>} value={form.effectiveDate} onChange={(v) => handleChange("effectiveDate", v)} />
                <Input label="Performance Cycle" value={form.performanceCycle} onChange={(v) => handleChange("performanceCycle", v)} placeholder="e.g. FY 2024-25" />
                <Input label="Issued By" value={form.issuedBy} onChange={(v) => handleChange("issuedBy", v)} />
              </div>

              <div className="mt-5">
                <label className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-1.5 block">Remarks</label>
                <textarea
                  className="w-full border border-slate-200 rounded-lg px-4 py-3 h-24 bg-slate-50/30 focus:ring-1 focus:ring-indigo-500 outline-none transition-all text-sm"
                  value={form.remarks}
                  onChange={(e) => handleChange("remarks", e.target.value)}
                  placeholder="Add any specific performance notes..."
                />
              </div>

              <div className="mt-6">
                <button
                  onClick={() => setShowPreview(true)}
                  className="w-full md:w-auto bg-indigo-600 hover:bg-indigo-700 text-white px-8 py-2.5 rounded-lg text-sm font-medium transition-all shadow-md flex items-center justify-center gap-2"
                >
                  <Eye size={16} /> Generate Preview
                </button>
              </div>
            </div>

            {/* History Section */}
            {sentLetters.length > 0 && (
              <div className="bg-white border border-slate-200 rounded-2xl p-6 shadow-sm">
                <h2 className="text-sm font-bold uppercase tracking-wider text-slate-400 mb-4" style={poppinsStyle}>Recent Revisions</h2>
                <div className="divide-y divide-slate-100">
                  {sentLetters.map((l, i) => (
                    <div key={i} className="py-4 flex justify-between items-center group">
                      <div>
                        <p className="font-semibold text-slate-800 text-sm">{l.employeeName}</p>
                        <p className="text-xs text-slate-400">Revised to {l.revisedSalary} • {l.sentAt}</p>
                      </div>
                      <div className="flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button onClick={() => { setForm(l); setShowPreview(true); }} className="p-2 text-slate-400 hover:text-indigo-600"><Eye size={16}/></button>
                        <button onClick={() => setSentLetters(prev => prev.filter((_, idx) => idx !== i))} className="p-2 text-slate-400 hover:text-red-600"><Trash2 size={16}/></button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Preview Column */}
          <div className="lg:col-span-1">
            {showPreview ? (
              <div className="bg-white border-2 border-indigo-100 rounded-2xl p-6 shadow-xl sticky top-8 animate-in fade-in slide-in-from-right-4 duration-300">
                <div className="flex items-center gap-2 text-indigo-600 mb-6">
                  <FileText size={20} />
                  <span className="font-bold text-xs uppercase tracking-widest">Document Preview</span>
                </div>

                <div className="space-y-4 text-slate-700 text-sm leading-relaxed">
                  <p>Dear <span className="font-bold">{form.employeeName || "Employee"}</span>,</p>
                  <p>
                    Based on your performance during <span className="font-semibold text-indigo-600">{form.performanceCycle || "the review period"}</span>, we are pleased to revise your compensation.
                  </p>
                  <div className="bg-slate-50 p-4 rounded-xl border border-slate-100 space-y-2">
                    <div className="flex justify-between"><span>Current:</span> <span className="font-medium">{form.currentSalary || "0"}</span></div>
                    <div className="flex justify-between text-green-600 font-bold"><span>Increment:</span> <span>+ {form.incrementAmount || "0"}</span></div>
                    <div className="pt-2 border-t flex justify-between font-bold text-slate-900"><span>Revised:</span> <span>{form.revisedSalary || calculateRevisedSalary()}</span></div>
                  </div>
                  <p>This revision is effective from <strong>{form.effectiveDate || "[Date]"}</strong>.</p>
                  <p className="text-xs text-slate-400 pt-4 border-t italic">Issued by: {form.issuedBy || "Human Resources"}</p>
                </div>

                <div className="mt-8 space-y-3">
                  <button 
                    onClick={handleSend}
                    className="w-full bg-green-600 hover:bg-green-700 text-white py-2.5 rounded-xl font-bold text-xs uppercase tracking-widest transition-all flex items-center justify-center gap-2"
                  >
                    <Send size={16} /> Finalize & Send
                  </button>
                  <button onClick={() => setShowPreview(false)} className="w-full text-slate-400 hover:text-slate-600 text-xs font-bold uppercase tracking-widest py-2">
                    Edit Details
                  </button>
                </div>
              </div>
            ) : (
              <div className="bg-slate-100/50 border-2 border-dashed border-slate-200 rounded-2xl p-12 text-center sticky top-8">
                <Eye className="mx-auto text-slate-300 mb-3" size={32} />
                <p className="text-slate-400 text-sm font-medium">Generate a preview to see<br/>the letter format here.</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function Input({ label, value, onChange, type = "text", icon, placeholder }) {
  return (
    <div className="space-y-1.5">
      <label className="text-[10px] font-bold uppercase tracking-widest text-slate-400">{label}</label>
      <div className="relative flex items-center">
        {icon && <div className="absolute left-3 text-slate-400">{icon}</div>}
        <input
          type={type}
          value={value}
          placeholder={placeholder}
          onChange={(e) => onChange(e.target.value)}
          className={`w-full border border-slate-200 rounded-lg ${icon ? 'pl-9' : 'px-4'} py-2 bg-slate-50/30 focus:ring-1 focus:ring-indigo-500 focus:bg-white outline-none transition-all text-sm`}
        />
      </div>
    </div>
  );
}